import React from 'react';
import { Check } from 'lucide-react';
import { ChecklistItemWithPhase } from '../types/index';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

interface ChecklistItemRowProps {
    item: ChecklistItemWithPhase;
    childId: number;
    checklistLoading: { [key: number]: boolean };
    onCheckChange: (itemId: number, newChecked: boolean) => void;
    onDelete?: (itemId: number) => void;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
    item,
    checklistLoading,
    onCheckChange,
    onDelete,
}) => {
    const { t } = useTranslation();
    const checked = item.isChecked;
    const displayName = item.nameKey
        ? translateWithFallback(t, item.nameKey, item.name)
        : item.name;

    return (
        <li className="flex items-center justify-between bg-[color-mix(in_srgb,var(--bg-color),white_50%)] px-4 py-3 border-b border-[color-mix(in_srgb,var(--text-color),transparent_92%)] last:border-b-0">
            <label className="flex items-center flex-1 min-w-0 cursor-pointer gap-3">
                {/* Custom checkbox — phase accent when checked (Figma-like) */}
                <span className="relative inline-flex h-5 w-5 flex-shrink-0 items-center justify-center">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => onCheckChange(item.id, e.target.checked)}
                        className="peer sr-only"
                    />
                    <span
                        className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${
                            checked ? 'border-transparent' : 'bg-[var(--bg-color)]'
                        }`}
                        style={
                            checked
                                ? { backgroundColor: 'var(--phase-accent, var(--accent-color))' }
                                : {
                                      borderColor:
                                          'color-mix(in srgb, var(--phase-accent, var(--accent-color)) 55%, transparent)',
                                  }
                        }
                        aria-hidden
                    >
                        {checked && <Check className="h-3.5 w-3.5 text-white stroke-[3]" />}
                    </span>
                </span>
                <span
                    className={
                        checked
                            ? 'line-through text-[color-mix(in_srgb,var(--text-color),transparent_50%)]'
                            : 'text-[var(--text-color)]'
                    }
                >
                    {displayName}
                </span>
                {checklistLoading[item.id] && (
                    <span className="ml-1 text-[color-mix(in_srgb,var(--text-color),transparent_40%)] text-sm">
                        {t('state.saving')}
                    </span>
                )}
            </label>

            {item.isCustom && onDelete && (
                <button
                    onClick={() => onDelete(item.id)}
                    className="text-[color-mix(in_srgb,var(--text-color),transparent_40%)] hover:opacity-80 text-xl leading-none px-3"
                    title="Delete custom item"
                    type="button"
                >
                    {'\u274C'}
                </button>
            )}
        </li>
    );
};

export default ChecklistItemRow;
